/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { Loader2, Play } from 'lucide-react'
import Image from 'next/image'
import React, { forwardRef, useEffect, useState } from 'react'

import { useCarousel } from '@/components/e-commerce/kits/kit-gallery-carousel/Carousel'
import { VimeoModalPlayer } from '@/components/e-commerce/kits/kit-gallery-carousel/vimeo/VimeoModalPlayer'
import { fetchVimeoMetadata, VimeoMetadata } from '@/lib/vimeo/metadata'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type CarouselVimeoItemProps = React.HTMLAttributes<HTMLDivElement> & {
  /** The Vimeo video ID to display the thumbnail for. */
  videoId: string
}

/**
 * An item for the carousel that points towards a Vimeo video. This component communicates with the Vimeo API to obtain
 * the thumbnail for the video, and displays it with a 'play' icon overlay on top to indicate that the carousel item is
 * a video that can be played. When the 'play' icon is clicked, a modal will open with the Vimeo player.
 */
const CarouselVimeoItem = forwardRef<HTMLDivElement, CarouselVimeoItemProps>(({ videoId, className }, ref) => {
  const { orientation } = useCarousel()
  const [vimeoMetadata, setVimeoMetadata] = useState<VimeoMetadata | null>(null)
  const [playerOpen, setPlayerOpen] = useState(false)

  useEffect(() => {
    if (!vimeoMetadata) {
      fetchVimeoMetadata(videoId).then((data) => setVimeoMetadata(data))
    }
  }, [vimeoMetadata, videoId])

  return (
    <div
      ref={ref}
      role={'group'}
      aria-roledescription={'slide'}
      className={cn('min-w-0 shrink-0 grow-0 basis-full', orientation === 'horizontal' ? 'pl-4' : 'pt-4', className)}
    >
      {playerOpen && vimeoMetadata && (
        <VimeoModalPlayer videoId={videoId} onClose={() => setPlayerOpen(false)} vimeoMetadata={vimeoMetadata} />
      )}
      <Play
        className={cn(
          'z-10 cursor-pointer absolute  top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
          'p-3 w-12 h-12 text-white bg-black/35 rounded-full border-2 border-white',
        )}
        onClick={() => setPlayerOpen(true)}
      />
      {vimeoMetadata && <Image src={vimeoMetadata.thumbnail_large} alt={''} fill className={'object-cover'} />}
      {!vimeoMetadata && (
        <div className={'absolute inset-0 flex items-center justify-center'}>
          <Loader2 className={'animate-spin'} size={14} />
        </div>
      )}
    </div>
  )
})
CarouselVimeoItem.displayName = 'CarouselVimeoItem'

export { CarouselVimeoItem }
